import {computed, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {catchError, EMPTY, finalize, interval, Observable, switchMap, tap} from 'rxjs';
import {dayString, isNightTour, sortChronological, Tour, TourResponse} from '../interfaces/tour.model';
import {UserService} from "../../../core/user/services/user.service";
import {InvoicesRestService} from "../../../core/invoices/services/invoices.rest.service";
import {ResponseModel} from "../../../core/base/response.model";
import {TrackingRestService} from "../api/tracking.rest.service";
import {DocRequest} from "../../../core/invoices/api/doc.request.model";

const BRANCH_NO = 21;
const FRESH_MS = 60_000; // data newer than this is not reloaded (e.g. when returning to the tab)

const toTours = (res: ResponseModel): Tour[] => (res.return_object as TourResponse).tour_information.tour;

@Injectable({providedIn: 'root'})
export class TrackingService {
  private readonly api = inject(TrackingRestService);
  private readonly invoices = inject(InvoicesRestService);
  private readonly userService = inject(UserService);

  readonly reloadIntervalMs = signal(5 * 60_000);

  readonly daysBack = signal<0 | 1>(0); /** 0 = today, 1 = yesterday */

  readonly loading = signal(true);
  readonly tours = signal<Tour[]>([]); // chronological order
  readonly lastUpdated = signal<Date | null>(null);
  readonly current = computed(() =>
    this.daysBack() === 0 ? this.tours().find(t => t.delivered_at === null) : undefined,
  );

  private readonly watchers = signal(0);
  private readonly reloadTick = signal(0);
  private loadedKey = '';
  private loadedIdf = '';
  private loadedAt = 0;

  constructor() {
    const request = computed(() => ({
      idf: this.userService.selectedIdf(),
      daysBack: this.daysBack(),
      tick: this.reloadTick(),
    }));

    toObservable(request)
    .pipe(
      switchMap(r => (r.idf ? this.fetchAll(r.idf, r.daysBack) : EMPTY)),
      takeUntilDestroyed(),
    )
    .subscribe();

    const pollMs = computed(() =>
      this.watchers() > 0 && this.daysBack() === 0 ? this.reloadIntervalMs() : 0,
    );

    toObservable(pollMs)
    .pipe(
      switchMap(ms => (ms > 0 ? interval(ms) : EMPTY)),
      switchMap(() => this.refreshCurrent()),
      takeUntilDestroyed(),
    )
    .subscribe();
  }

  watch(): () => void {
    this.watchers.update(n => n + 1);
    if (Date.now() - this.loadedAt >= FRESH_MS) this.reloadTick.update(n => n + 1);
    return () => this.watchers.update(n => n - 1);
  }

  downloadInvoice(docNo: number, tourId: string): void {
    const back = this.daysBack() === 1 || isNightTour(tourId) ? 1 : 0;
    this.invoices.getDocument(
      new DocRequest({
        docDate: dayString(back),
        docNr: docNo,
        docType: 'INVOICE',
        idf: Number(this.loadedIdf),
        vz: BRANCH_NO,
      }),
    );
  }

  private fetchAll(idf: string, daysBack: 0 | 1): Observable<unknown> {
    console.log('[TrackingService] Current IDF:', idf);

    const key = `${idf}|${daysBack}`;
    const sameData = key === this.loadedKey;

    if (sameData && Date.now() - this.loadedAt < FRESH_MS) {
      this.loading.set(false);
      return EMPTY;
    }

    if (!sameData) {
      this.tours.set([]);
      this.lastUpdated.set(null);
    }
    this.loading.set(true);

    return this.api
    .getTours({branch_no: BRANCH_NO, idf, request_date: dayString(daysBack), details_desired: true})
    .pipe(
      tap(res => {
        this.loadedKey = key;
        this.loadedIdf = idf;
        this.loadedAt = Date.now();
        this.tours.set(sortChronological(toTours(res)));
        this.lastUpdated.set(new Date());
      }),
      catchError(() => EMPTY),
      finalize(() => this.loading.set(false)),
    );
  }

  private refreshCurrent(): Observable<unknown> {
    const cur = this.current();
    if (!cur) return EMPTY;

    return this.api
    .getTours({
      branch_no: BRANCH_NO,
      idf: this.loadedIdf,
      request_date: dayString(),
      details_desired: false,
      tour_id: cur.tour_id,
    })
    .pipe(
      tap(res => {
        const fresh = toTours(res)[0];
        if (!fresh) return;
        const {
          expected_time_of_arrival_min, expected_time_of_arrival_max,
          delivered_at, delay_in_minutes, delay_information,
        } = fresh;

        this.tours.update(list =>
          list.map(t =>
            t.tour_id === cur.tour_id
              ? { ...t, expected_time_of_arrival_min, expected_time_of_arrival_max, delivered_at, delay_in_minutes, delay_information }
              : t,
          ),
        );
        this.lastUpdated.set(new Date());
      }),
      catchError(() => EMPTY),
    );
  }
}
